import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { getUser, updateUser, uploadAvatar, deleteScore, getUserBadges, getFriends, sendFriendRequest, respondToFriendRequest, removeFriend, getPendingFriendPosts, respondToFriendPost } from '../api';
import { useAuth } from '../context/AuthContext';
import {
  Avatar, PageHeader, Spinner, LoadingPage, ErrorMsg, SuccessMsg,
  ScoreBadge, RankBadge, EmptyState, formatDate
} from '../components/UI';

function EditProfileModal({ profile, onClose, onSaved }) {
  const { user, refreshProfile } = useAuth();

  // Username / handicap state
  const [username, setUsername] = useState(profile.username || '');
  const [handicap, setHandicap] = useState(profile.handicap ?? '');

  // Avatar upload state
  const [avatarFile, setAvatarFile] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState(profile.avatar_url || null);
  const [avatarUploading, setAvatarUploading] = useState(false);
  const fileInputRef = useRef(null);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // When a file is picked, show a local preview immediately
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setAvatarFile(file);
    setAvatarPreview(URL.createObjectURL(file));
    setError('');
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    const profileUpdates = {};
    if (username !== profile.username) profileUpdates.username = username;
    if (handicap !== (profile.handicap ?? '')) {
      profileUpdates.handicap = handicap !== '' ? parseFloat(handicap) : undefined;
    }

    // Remove undefined keys
    Object.keys(profileUpdates).forEach(k => profileUpdates[k] === undefined && delete profileUpdates[k]);

    const hasProfileChanges = Object.keys(profileUpdates).length > 0;
    const hasNewAvatar = !!avatarFile;

    if (!hasProfileChanges && !hasNewAvatar) {
      setError('No changes to save.');
      return;
    }

    setLoading(true);
    try {
      // Upload avatar first if a new file was selected
      if (hasNewAvatar) {
        setAvatarUploading(true);
        await uploadAvatar(user.id, avatarFile);
        setAvatarUploading(false);
      }

      // Then save any text field changes
      if (hasProfileChanges) {
        await updateUser(user.id, profileUpdates);
      }

      await refreshProfile();
      setSuccess('Profile updated!');
      setTimeout(() => onSaved(), 1200);
    } catch (err) {
      setError(err.message);
      setAvatarUploading(false);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-end sm:items-center justify-center z-50 p-4">
      <div className="card w-full max-w-sm p-6 fade-in">
        <h3 className="font-serif font-bold text-xl text-green-900 mb-5">Edit Profile</h3>
        <form onSubmit={handleSave} className="space-y-4">

          {/* ── Avatar upload ── */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-2 uppercase tracking-wide">
              Profile Photo <span className="normal-case text-gray-400">(optional)</span>
            </label>
            <div className="flex items-center gap-4">
              {/* Live preview */}
              <div className="w-16 h-16 rounded-full border-2 border-amber-200 overflow-hidden flex-shrink-0 bg-green-800 flex items-center justify-center">
                {avatarPreview ? (
                  <img src={avatarPreview} alt="Preview" className="w-full h-full object-cover" />
                ) : (
                  <span className="text-amber-100 font-semibold text-xl">
                    {profile.username?.slice(0, 2).toUpperCase() || '?'}
                  </span>
                )}
              </div>
              <div className="flex-1">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/jpg,image/png,image/gif,image/webp,image/heic"
                  onChange={handleFileChange}
                  className="hidden"
                  id="avatar-upload"
                />
                <label
                  htmlFor="avatar-upload"
                  className="btn-secondary cursor-pointer text-xs inline-block"
                >
                  {avatarFile ? '📷 Change Photo' : '📷 Upload Photo'}
                </label>
                {avatarFile && (
                  <p className="text-xs text-green-700 mt-1 truncate max-w-[160px]">{avatarFile.name}</p>
                )}
                <p className="text-xs text-gray-400 mt-1">JPG, PNG, GIF, WEBP or HEIC · Max 5 MB</p>
              </div>
            </div>
          </div>

          {/* ── Username ── */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1.5 uppercase tracking-wide">Display Name</label>
            <input
              type="text"
              className="input-field"
              value={username}
              onChange={e => setUsername(e.target.value)}
              minLength={3}
              maxLength={20}
              placeholder="Your username"
            />
            <p className="text-xs text-gray-400 mt-1">3–20 characters. Letters, numbers, and underscores only.</p>
          </div>

          {/* ── Handicap ── */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1.5 uppercase tracking-wide">
              Handicap <span className="normal-case text-gray-400">(optional)</span>
            </label>
            <input
              type="number"
              className="input-field"
              value={handicap}
              onChange={e => setHandicap(e.target.value)}
              placeholder="e.g. 12.3"
              min={-10}
              max={54}
              step={0.1}
            />
          </div>

          <ErrorMsg message={error} />
          <SuccessMsg message={success} />

          <div className="flex gap-3">
            <button type="button" onClick={onClose} className="btn-secondary flex-1">Cancel</button>
            <button
              type="submit"
              className="btn-primary flex-1 flex items-center justify-center gap-2"
              disabled={loading}
            >
              {loading ? (
                <><Spinner size="sm" /> {avatarUploading ? 'Uploading...' : 'Saving...'}</>
              ) : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Confirmation dialog before deleting a score
function DeleteScoreConfirm({ score, onCancel, onConfirmed }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleDelete = async () => {
    setLoading(true);
    setError('');
    try {
      await deleteScore(score.score_id);
      onConfirmed();
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-end sm:items-center justify-center z-50 p-4">
      <div className="card w-full max-w-sm p-6 fade-in">
        <h3 className="font-serif font-bold text-xl text-green-900 mb-2">Delete Score?</h3>
        <p className="text-sm text-gray-500 mb-1">
          This will permanently remove your score of{' '}
          <span className="font-semibold text-green-800">{score.total_score}</span> (
          <span className={score.over_under_par < 0 ? 'text-green-700 font-semibold' : score.over_under_par > 0 ? 'text-red-600 font-semibold' : 'font-semibold'}>
            {score.over_under_par === 0 ? 'E' : score.over_under_par > 0 ? `+${score.over_under_par}` : score.over_under_par}
          </span>
          ) on the <span className="capitalize font-medium">{score.tee_color}</span> tees.
        </p>
        <p className="text-xs text-red-500 mb-5">This action cannot be undone.</p>
        <ErrorMsg message={error} />
        <div className="flex gap-3">
          <button onClick={onCancel} className="btn-secondary flex-1" disabled={loading}>Keep It</button>
          <button
            onClick={handleDelete}
            className="flex-1 bg-red-600 text-white font-medium py-2.5 px-5 rounded-lg hover:bg-red-700 transition-all duration-200 text-sm flex items-center justify-center gap-2"
            disabled={loading}
          >
            {loading ? <Spinner size="sm" /> : '🗑 Delete'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Share a score from the profile page ──────────────────────────────────────
function ShareScoreButton({ score, profile }) {
  const [copied, setCopied] = useState(false);

  const scoreDisplay = score.over_under_par === 0 ? 'E'
    : score.over_under_par > 0 ? `+${score.over_under_par}`
    : `${score.over_under_par}`;

  const rankText = score.place ? `ranked #${score.place}` : '';
  const url = `https://golfboardscores.com/leaderboard/course/${score.course_id}`;
  const text = `🏌️ ${profile.username} shot ${score.total_score} (${scoreDisplay}) on the ${score.tee_color} tees at ${score.course_name}${rankText ? ` — ${rankText}` : ''}!\nCheck the leaderboard on GolfBoard Scores: ${url}`;

  const handleCopy = async (e) => {
    e.stopPropagation();
    try { await navigator.clipboard.writeText(text); }
    catch {
      const el = document.createElement('textarea');
      el.value = text; document.body.appendChild(el); el.select();
      document.execCommand('copy'); document.body.removeChild(el);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <button
      onClick={handleCopy}
      className={`flex-shrink-0 flex items-center gap-1 text-xs px-2 py-1.5 rounded-lg border transition-all duration-200 ${
        copied
          ? 'bg-green-50 border-green-300 text-green-700 font-semibold'
          : 'bg-white border-gray-200 text-gray-300 hover:border-green-400 hover:text-green-700'
      }`}
      title="Share this score"
    >
      {copied ? '✓' : '🔗'}
    </button>
  );
}

// ── Badge emoji map ───────────────────────────────────────────────────────────
const BADGE_EMOJI = {
  first_tee:         '🏌️',
  getting_warmed_up: '🌡️',
  regular_golfer:    '📋',
  dedicated_player:  '🎯',
  course_explorer:   '⛳',
  local_traveler:    '✈️',
  course_conqueror:  '🗺️',
  city_contender:    '🏙️',
  state_contender:   '📍',
  course_champion:   '🥇',
  regional_elite:    '👑',
  verified_round:    '✅',
  trusted_player:    '⭐',
  returning_golfer:  '🔄',
  community_regular: '👥',
  city_hopper:       '🌆',
  city_champion:     '🏆',
  state_champion:    '🎖️',
  national_champion: '🌎',
  course_pioneer:    '🚩',
  hot_streak:        '🔥',
  playing_partner:   '🤝',
  caddie_crew:       '⛳',
};

const CATEGORY_ORDER = ['rounds', 'courses', 'leaderboard', 'verification', 'community', 'streaks', 'pioneer', 'social'];
const CATEGORY_LABELS = {
  rounds:       '🏌️ Rounds Played',
  courses:      '⛳ Course Explorer',
  leaderboard:  '🏆 Leaderboard',
  verification: '✅ Verification',
  community:    '👥 Community',
  streaks:      '🔥 Streaks',
  pioneer:      '🚩 Pioneer',
  social:       '🤝 Social',
};

// All badge definitions
const ALL_BADGES = [
  { id: 'first_tee',         name: 'First Tee',         description: 'Post your first round score.',                                   category: 'rounds' },
  { id: 'getting_warmed_up', name: 'Getting Warmed Up', description: 'Post 5 total round scores.',                                     category: 'rounds' },
  { id: 'regular_golfer',    name: 'Regular Golfer',    description: 'Post 10 total round scores.',                                    category: 'rounds' },
  { id: 'dedicated_player',  name: 'Dedicated Player',  description: 'Post 25 total round scores.',                                    category: 'rounds' },
  { id: 'course_explorer',   name: 'Course Explorer',   description: 'Post scores from 2 different courses.',                          category: 'courses' },
  { id: 'local_traveler',    name: 'Local Traveler',    description: 'Post scores from 5 different courses.',                          category: 'courses' },
  { id: 'course_conqueror',  name: 'Course Conqueror',  description: 'Post scores from 10 different courses.',                         category: 'courses' },
  { id: 'city_contender',    name: 'City Contender',    description: 'Finish Top 5 on the City monthly leaderboard.',                  category: 'leaderboard' },
  { id: 'state_contender',   name: 'State Contender',   description: 'Finish Top 5 on the State monthly leaderboard.',                 category: 'leaderboard' },
  { id: 'course_champion',   name: 'Course Champion',   description: 'Finish Top 5 on a Course monthly leaderboard.',                  category: 'leaderboard' },
  { id: 'regional_elite',    name: 'Regional Elite',    description: 'Reach Top 5 on any all-time regional leaderboard.',              category: 'leaderboard' },
  { id: 'city_champion',     name: 'City Champion',     description: 'Finish #1 on the City monthly leaderboard.',                    category: 'leaderboard' },
  { id: 'state_champion',    name: 'State Champion',    description: 'Finish #1 on the State monthly leaderboard.',                   category: 'leaderboard' },
  { id: 'national_champion', name: 'National Champion', description: 'Finish #1 on the Country monthly leaderboard.',                 category: 'leaderboard' },
  { id: 'verified_round',    name: 'Verified Round',    description: 'Have one score verified by another user.',                       category: 'verification' },
  { id: 'trusted_player',    name: 'Trusted Player',    description: 'Have 10 scores verified by other users.',                       category: 'verification' },
  { id: 'returning_golfer',  name: 'Returning Golfer',  description: 'Post a score on a different day than your first.',              category: 'community' },
  { id: 'community_regular', name: 'Community Regular', description: 'Post scores on 10 different calendar days.',                    category: 'community' },
  { id: 'city_hopper',       name: 'City Hopper',       description: 'Post scores in multiple cities.',                               category: 'community' },
  { id: 'course_pioneer',    name: 'Course Pioneer',    description: 'Post the first recorded score for a golf course.',              category: 'pioneer' },
  { id: 'hot_streak',        name: 'Hot Streak',        description: 'Top 10 on the same course for 5 consecutive rounds.',           category: 'streaks' },
  { id: 'playing_partner',   name: 'Playing Partner',   description: 'Add your first friend on GolfBoard.',                           category: 'social' },
  { id: 'caddie_crew',       name: 'Caddie Crew',       description: 'Build a crew of 5 friends on GolfBoard.',                       category: 'social' },
];

function BadgeIcon({ badgeId, earned = true }) {
  const emoji = BADGE_EMOJI[badgeId] || '🏅';
  return (
    <div
      className={`w-12 h-12 rounded-2xl flex items-center justify-center text-2xl flex-shrink-0 transition-all ${
        earned ? 'bg-amber-50 shadow-sm' : 'bg-gray-100'
      }`}
      style={{ opacity: earned ? 1 : 0.35, filter: earned ? 'none' : 'grayscale(1)' }}
    >
      {emoji}
    </div>
  );
}

function ShareBadgeButton({ badge, username }) {
  const [copied, setCopied] = useState(false);
  const text = `🏅 ${username} just earned the "${badge.name}" badge on GolfBoard Scores!\n${badge.description}\nhttps://golfboardscores.com`;
  const handleCopy = async (e) => {
    e.stopPropagation();
    try { await navigator.clipboard.writeText(text); }
    catch {
      const el = document.createElement('textarea');
      el.value = text; document.body.appendChild(el); el.select();
      document.execCommand('copy'); document.body.removeChild(el);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <button
      onClick={handleCopy}
      className={`text-xs px-2 py-1 rounded-lg border transition-all duration-200 flex-shrink-0 ${
        copied
          ? 'bg-green-50 border-green-300 text-green-700 font-semibold'
          : 'bg-white border-gray-200 text-gray-400 hover:border-amber-400 hover:text-amber-700'
      }`}
      title="Share this badge"
    >
      {copied ? '✓ Copied' : '🔗 Share'}
    </button>
  );
}

function BadgesSection({ userId, username }) {
  const [badges, setBadges] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAll, setShowAll] = useState(false);
  const [activeCategory, setActiveCategory] = useState('all');
  const [tooltip, setTooltip] = useState(null); // badge id for locked tooltip

  useEffect(() => {
    getUserBadges(userId)
      .then(setBadges)
      .catch(() => setBadges([]))
      .finally(() => setLoading(false));
  }, [userId]);

  if (loading) return (
    <div className="flex justify-center py-8"><Spinner size="md" /></div>
  );

  const earnedIds = new Set((badges || []).map(b => b.badge_id));
  const earnedCount = earnedIds.size;

  // Build merged list: earned badges (with date) + uneaned from ALL_BADGES
  const earnedMap = {};
  (badges || []).forEach(b => { earnedMap[b.badge_id] = b; });

  const allWithStatus = ALL_BADGES.map(b => ({
    ...b,
    earned: earnedIds.has(b.id),
    earned_at: earnedMap[b.id]?.earned_at || null,
  }));

  const categories = ['all', ...CATEGORY_ORDER];
  const visible = activeCategory === 'all'
    ? allWithStatus
    : allWithStatus.filter(b => b.category === activeCategory);

  // In collapsed view show earned first, then locked, up to 12
  const displayed = showAll ? visible : visible.slice(0, 12);

  return (
    <div>
      {/* Header row */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <h2 className="font-serif font-semibold text-green-900 text-lg">Badges</h2>
          <span className="bg-amber-100 text-amber-800 text-xs font-bold px-2 py-0.5 rounded-full">
            {earnedCount} / {ALL_BADGES.length}
          </span>
        </div>
        {visible.length > 12 && (
          <button
            onClick={() => setShowAll(s => !s)}
            className="text-xs text-green-700 hover:text-green-900 font-medium transition-colors"
          >
            {showAll ? 'Show less' : `Show all ${visible.length}`}
          </button>
        )}
      </div>

      {/* Category filter pills */}
      <div className="flex gap-1.5 flex-wrap mb-4">
        {categories.map(cat => (
          <button
            key={cat}
            onClick={() => setActiveCategory(cat)}
            className={`text-xs px-3 py-1.5 rounded-full border font-medium transition-all duration-150 ${
              activeCategory === cat
                ? 'bg-green-800 text-white border-green-800'
                : 'bg-white text-gray-500 border-gray-200 hover:border-green-400 hover:text-green-800'
            }`}
          >
            {cat === 'all' ? `All (${earnedCount}/${ALL_BADGES.length})` : CATEGORY_LABELS[cat]}
          </button>
        ))}
      </div>

      {/* Badge grid */}
      {earnedCount === 0 && activeCategory === 'all' ? (
        <div className="card p-6 text-center">
          <div className="text-3xl mb-2">🏅</div>
          <p className="font-medium text-green-900 text-sm mb-1">No badges yet</p>
          <p className="text-xs text-gray-400">Post scores, climb leaderboards, and explore courses to earn badges.</p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-3">
            {displayed.map(badge => (
              <div
                key={badge.id}
                className={`relative group flex flex-col items-center gap-1.5 p-3 rounded-2xl border transition-all duration-200 cursor-default ${
                  badge.earned
                    ? 'bg-white border-amber-200 shadow-sm hover:shadow-md hover:border-amber-400'
                    : 'bg-gray-50 border-gray-100'
                }`}
                onMouseEnter={() => !badge.earned && setTooltip(badge.id)}
                onMouseLeave={() => setTooltip(null)}
              >
                <BadgeIcon badgeId={badge.id} earned={badge.earned} />
                <span className={`text-center leading-tight font-medium text-xs ${badge.earned ? 'text-green-900' : 'text-gray-400'}`}
                  style={{ fontSize: '0.65rem', lineHeight: '1.3' }}>
                  {badge.name}
                </span>
                {badge.earned && (
                  <ShareBadgeButton badge={badge} username={username} />
                )}
                {/* Locked tooltip */}
                {!badge.earned && tooltip === badge.id && (
                  <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 z-20 w-44 bg-green-900 text-white text-xs rounded-xl px-3 py-2 shadow-lg pointer-events-none">
                    <div className="font-semibold mb-0.5">🔒 {badge.name}</div>
                    <div className="text-green-300">{badge.description}</div>
                    <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-green-900" />
                  </div>
                )}
              </div>
            ))}
          </div>
          {!showAll && visible.length > 12 && (
            <button
              onClick={() => setShowAll(true)}
              className="mt-3 w-full text-xs text-gray-400 hover:text-green-800 py-2 border border-dashed border-gray-200 hover:border-green-300 rounded-xl transition-colors"
            >
              + {visible.length - 12} more badges
            </button>
          )}
        </>
      )}
    </div>
  );
}

// ── Pending friend-posted scores notification ─────────────────────────────────
function PendingFriendPostsSection({ onCountChange }) {
  const [posts,          setPosts]          = useState([]);
  const [loading,        setLoading]        = useState(true);
  const [actionLoading,  setActionLoading]  = useState(null); // score_id being actioned
  const [error,          setError]          = useState('');

  const load = () => {
    setLoading(true);
    getPendingFriendPosts()
      .then(data => {
        setPosts(data || []);
        onCountChange?.(data?.length || 0);
      })
      .catch(() => setPosts([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const handle = async (scoreId, action) => {
    setActionLoading(scoreId); setError('');
    try {
      await respondToFriendPost(scoreId, action);
      load();
    } catch (err) {
      setError(err.message);
    } finally {
      setActionLoading(null);
    }
  };

  if (loading) return <div className="flex justify-center py-4"><Spinner size="sm" /></div>;
  if (posts.length === 0) return null;

  return (
    <div className="card p-5 mb-5 border-amber-200">
      <div className="flex items-center gap-2 mb-3">
        <h2 className="font-serif font-semibold text-green-900 text-lg">📤 Scores Posted for You</h2>
        <span className="bg-amber-100 text-amber-800 text-xs font-bold px-2 py-0.5 rounded-full">
          {posts.length}
        </span>
      </div>
      <p className="text-xs text-gray-400 mb-4">
        A friend posted these rounds on your behalf. Accept to add them to your record (they'll count as verified), or decline to remove them.
      </p>
      {error && <p className="text-xs text-red-500 mb-3">{error}</p>}
      <div className="space-y-3">
        {posts.map(p => {
          const tee    = p.tees;
          const course = tee?.courses;
          const overUnder = p.over_under_par;
          const ouDisplay = overUnder === 0 ? 'E' : overUnder > 0 ? `+${overUnder}` : `${overUnder}`;
          return (
            <div key={p.id} className="bg-amber-50 border border-amber-100 rounded-xl p-3">
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-green-900">
                    {course?.name || 'Unknown course'}
                  </p>
                  <p className="text-xs text-gray-500 mt-0.5">
                    {[course?.city, course?.state].filter(Boolean).join(', ')}
                  </p>
                  <div className="flex flex-wrap items-center gap-2 mt-1.5">
                    {tee?.color && (
                      <span className="inline-flex items-center gap-1 bg-white border border-gray-200 text-gray-500 text-xs rounded-full px-2 py-0.5">
                        <span className="w-2 h-2 rounded-full border border-gray-300 flex-shrink-0"
                          style={{ backgroundColor: tee.color.toLowerCase() === 'white' ? '#f0f0f0' : tee.color.toLowerCase() }} />
                        <span className="capitalize">{tee.color}</span>
                        <span className="text-gray-300">·</span>
                        <span>{tee.yardage?.toLocaleString()} yds</span>
                      </span>
                    )}
                    <span className="text-sm font-bold text-green-900">{p.total_score}</span>
                    <span className={`text-sm font-semibold ${overUnder < 0 ? 'text-green-700' : overUnder > 0 ? 'text-red-600' : 'text-gray-600'}`}>
                      ({ouDisplay})
                    </span>
                  </div>
                  <p className="text-xs text-gray-400 mt-1">
                    Posted by <span className="font-medium text-green-800">{p.poster?.username || 'a friend'}</span>
                    {' '}· {formatDate(p.date_posted)}
                  </p>
                </div>
                <div className="flex gap-2 flex-shrink-0 mt-1">
                  <button
                    onClick={() => handle(p.id, 'accepted')}
                    disabled={actionLoading === p.id}
                    className="text-xs px-3 py-1.5 rounded-lg bg-green-700 text-white hover:bg-green-600 transition-all flex items-center gap-1"
                  >
                    {actionLoading === p.id ? <Spinner size="sm" /> : '✓ Accept'}
                  </button>
                  <button
                    onClick={() => handle(p.id, 'declined')}
                    disabled={actionLoading === p.id}
                    className="text-xs px-3 py-1.5 rounded-lg border border-gray-200 text-gray-500 hover:border-red-300 hover:text-red-500 transition-all"
                  >
                    Decline
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Friends section ───────────────────────────────────────────────────────────
function FriendsSection({ isOwn, viewedUserId, authUserId }) {
  const [friendsData, setFriendsData] = useState(null);
  const [loading, setLoading]         = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError]             = useState('');

  const loadFriends = () => {
    setLoading(true);
    getFriends()
      .then(setFriendsData)
      .catch(err => setError(err.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => { loadFriends(); }, []);

  if (loading) return <div className="flex justify-center py-6"><Spinner /></div>;

  // For own profile: show full management UI
  // For other profiles: show friendship status + action button
  if (!isOwn) {
    // Determine relationship from auth user's perspective
    const allFriends     = friendsData?.friends          || [];
    const pendingSent     = friendsData?.pending_sent     || [];
    const pendingReceived = friendsData?.pending_received || [];

    const isFriend        = allFriends.some(f => f.user.id === viewedUserId);
    // I sent a request TO the viewed user — they are in MY pending_sent list
    const iSentRequest    = pendingSent.some(f => f.user.id === viewedUserId);
    // They sent a request TO me — they are in MY pending_received list
    const theySentRequest = pendingReceived.find(f => f.user.id === viewedUserId);

    const handleSend = async () => {
      setActionLoading(true); setError('');
      try { await sendFriendRequest(viewedUserId); loadFriends(); }
      catch (err) { setError(err.message); }
      finally { setActionLoading(false); }
    };
    const handleRemove = async () => {
      setActionLoading(true); setError('');
      try { await removeFriend(viewedUserId); loadFriends(); }
      catch (err) { setError(err.message); }
      finally { setActionLoading(false); }
    };
    // Only callable when theySentRequest is truthy — passes THEIR id as requester
    const handleRespond = async (action) => {
      if (!theySentRequest) return;
      setActionLoading(true); setError('');
      try { await respondToFriendRequest(viewedUserId, action); loadFriends(); }
      catch (err) { setError(err.message); }
      finally { setActionLoading(false); }
    };

    return (
      <div className="mb-2">
        {error && <p className="text-xs text-red-500 mb-2">{error}</p>}
        {isFriend ? (
          <button
            onClick={handleRemove}
            disabled={actionLoading}
            className="text-xs px-3 py-1.5 rounded-lg border border-green-300 bg-green-50 text-green-800 hover:bg-red-50 hover:border-red-300 hover:text-red-600 transition-all flex items-center gap-1.5"
          >
            {actionLoading ? <Spinner size="sm" /> : '✓ Friends · Remove'}
          </button>
        ) : iSentRequest ? (
          // I sent a request to them — they need to accept, I can only cancel
          <button
            onClick={handleRemove}
            disabled={actionLoading}
            className="text-xs px-3 py-1.5 rounded-lg border border-gray-200 text-gray-500 hover:border-red-300 hover:text-red-500 transition-all flex items-center gap-1.5"
          >
            {actionLoading ? <Spinner size="sm" /> : '⏳ Request Sent · Cancel'}
          </button>
        ) : theySentRequest ? (
          // They sent a request to me — I can accept or decline
          <div className="flex gap-2">
            <button onClick={() => handleRespond('accepted')} disabled={actionLoading}
              className="text-xs px-3 py-1.5 rounded-lg bg-green-700 text-white hover:bg-green-600 transition-all flex items-center gap-1">
              {actionLoading ? <Spinner size="sm" /> : '✓ Accept'}
            </button>
            <button onClick={() => handleRespond('declined')} disabled={actionLoading}
              className="text-xs px-3 py-1.5 rounded-lg border border-gray-200 text-gray-500 hover:border-red-300 hover:text-red-500 transition-all">
              Decline
            </button>
          </div>
        ) : (
          <button
            onClick={handleSend}
            disabled={actionLoading}
            className="text-xs px-3 py-1.5 rounded-lg border border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100 transition-all flex items-center gap-1.5"
          >
            {actionLoading ? <Spinner size="sm" /> : '+ Add Friend'}
          </button>
        )}
      </div>
    );
  }

  // Own profile: full friends management
  const friends         = friendsData?.friends || [];
  const pendingReceived = friendsData?.pending_received || [];
  const pendingSent     = friendsData?.pending_sent || [];

  const handleRespond = async (requesterId, action) => {
    setActionLoading(true); setError('');
    try { await respondToFriendRequest(requesterId, action); loadFriends(); }
    catch (err) { setError(err.message); }
    finally { setActionLoading(false); }
  };
  const handleRemove = async (friendUserId) => {
    setActionLoading(true); setError('');
    try { await removeFriend(friendUserId); loadFriends(); }
    catch (err) { setError(err.message); }
    finally { setActionLoading(false); }
  };

  return (
    <div>
      {error && <p className="text-xs text-red-500 mb-3">{error}</p>}

      {/* Pending received requests */}
      {pendingReceived.length > 0 && (
        <div className="mb-4">
          <h3 className="text-xs font-semibold text-amber-700 uppercase tracking-widest mb-2">
            Friend Requests ({pendingReceived.length})
          </h3>
          <div className="space-y-2">
            {pendingReceived.map(f => (
              <div key={f.friendship_id} className="flex items-center gap-3 bg-amber-50 border border-amber-100 rounded-xl px-3 py-2.5">
                <Link to={`/profile/${f.user.id}`} className="flex items-center gap-2 flex-1 min-w-0 hover:opacity-80 transition-opacity">
                  <Avatar url={f.user.avatar_url} username={f.user.username} size="sm" />
                  <span className="text-sm font-medium text-green-900 truncate">{f.user.username}</span>
                </Link>
                <div className="flex gap-1.5 flex-shrink-0">
                  <button onClick={() => handleRespond(f.user.id, 'accepted')} disabled={actionLoading}
                    className="text-xs px-2.5 py-1 rounded-lg bg-green-700 text-white hover:bg-green-600 transition-all">
                    Accept
                  </button>
                  <button onClick={() => handleRespond(f.user.id, 'declined')} disabled={actionLoading}
                    className="text-xs px-2.5 py-1 rounded-lg border border-gray-200 text-gray-500 hover:border-red-300 hover:text-red-500 transition-all">
                    Decline
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Friends list */}
      {friends.length === 0 && pendingReceived.length === 0 ? (
        <div className="text-center py-6">
          <div className="text-3xl mb-2">🤝</div>
          <p className="text-sm font-medium text-green-900 mb-1">No friends yet</p>
          <p className="text-xs text-gray-400">Find golfers on the <Link to="/people" className="text-green-700 underline">People</Link> page and add them as friends.</p>
        </div>
      ) : (
        <>
          {friends.length > 0 && (
            <div className="space-y-2">
              {friends.map(f => (
                <div key={f.friendship_id} className="flex items-center gap-3 rounded-xl px-2 py-2 hover:bg-amber-50/60 transition-colors">
                  <Link to={`/profile/${f.user.id}`} className="flex items-center gap-2 flex-1 min-w-0 hover:opacity-80 transition-opacity">
                    <Avatar url={f.user.avatar_url} username={f.user.username} size="sm" />
                    <span className="text-sm font-medium text-green-900 truncate">{f.user.username}</span>
                  </Link>
                  <button
                    onClick={() => handleRemove(f.user.id)}
                    disabled={actionLoading}
                    className="flex-shrink-0 text-xs text-gray-300 hover:text-red-500 transition-colors px-1 py-1"
                    title="Remove friend"
                  >✕</button>
                </div>
              ))}
            </div>
          )}
          {pendingSent.length > 0 && (
            <div className="mt-3 pt-3 border-t border-amber-50">
              <p className="text-xs text-gray-400 mb-2">Pending sent ({pendingSent.length})</p>
              <div className="space-y-1.5">
                {pendingSent.map(f => (
                  <div key={f.friendship_id} className="flex items-center gap-2 px-2 py-1.5">
                    <Link to={`/profile/${f.user.id}`} className="flex items-center gap-2 flex-1 min-w-0 hover:opacity-80">
                      <Avatar url={f.user.avatar_url} username={f.user.username} size="sm" />
                      <span className="text-sm text-gray-500 truncate">{f.user.username}</span>
                    </Link>
                    <span className="text-xs text-amber-600 flex-shrink-0">⏳ Pending</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default function ProfilePage() {
  const { userId } = useParams();
  const { user: authUser } = useAuth();
  const navigate = useNavigate();

  const [profile, setProfile] = useState(null);
  const [scores, setScores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEdit, setShowEdit] = useState(false);
  const [scoreToDelete, setScoreToDelete] = useState(null);
  const [pendingCount, setPendingCount] = useState(0);
  const [pendingPostCount, setPendingPostCount] = useState(0);

  const isOwn = authUser?.id === userId;

  const fetchData = async () => {
    try {
      const data = await getUser(userId);
      setProfile(data.user);
      setScores(data.scores || []);
    } catch {
      navigate('/');
    } finally {
      setLoading(false);
    }
  };

  // Load pending request count for notification dot (own profile only)
  useEffect(() => {
    if (!isOwn) return;
    getFriends()
      .then(data => setPendingCount(data?.pending_received?.length || 0))
      .catch(() => {});
  }, [isOwn]);

  useEffect(() => { fetchData(); }, [userId]);

  if (loading) return <LoadingPage />;
  if (!profile) return null;

  // Group scores by course
  const byCourse = {};
  scores.forEach(s => {
    if (!byCourse[s.course_id]) {
      byCourse[s.course_id] = {
        course_id: s.course_id,
        course_name: s.course_name,
        city: s.city,
        state: s.state,
        country: s.country,
        scores: [],
      };
    }
    byCourse[s.course_id].scores.push(s);
  });

  return (
    <div className="page-container">
      {!isOwn && <PageHeader back title="" />}

      {/* Profile header */}
      <div className="card p-5 mb-5 flex items-center gap-4">
        <Avatar url={profile.avatar_url} username={profile.username} size="lg" />
        <div className="flex-1 min-w-0">
          <h1 className="font-serif font-bold text-2xl text-green-900 truncate">{profile.username}</h1>
          <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1">
            {profile.handicap != null && (
              <span className="text-sm text-gray-500">
                <span className="font-medium text-green-800">{profile.handicap}</span> handicap
              </span>
            )}
            <span className="text-sm text-gray-400">
              Joined {formatDate(profile.join_date)}
            </span>
          </div>
          <div className="flex gap-3 mt-1">
            <span className="text-xs text-gray-500">
              <span className="font-semibold text-green-800">{scores.length}</span> score{scores.length !== 1 ? 's' : ''}
            </span>
            <span className="text-xs text-gray-500">
              <span className="font-semibold text-green-800">{Object.keys(byCourse).length}</span> course{Object.keys(byCourse).length !== 1 ? 's' : ''}
            </span>
          </div>
          {/* Friend action button (shown when viewing another user's profile) */}
          {!isOwn && authUser && (
            <div className="mt-2">
              <FriendsSection isOwn={false} viewedUserId={userId} authUserId={authUser.id} />
            </div>
          )}
        </div>
        {isOwn && (
          <button onClick={() => setShowEdit(true)} className="btn-secondary flex-shrink-0 text-xs">
            Edit Profile
          </button>
        )}
      </div>

      {/* Pending friend-posted scores — own profile only */}
      {isOwn && (
        <PendingFriendPostsSection onCountChange={setPendingPostCount} />
      )}

      {/* Badges */}
      <div className="card p-5 mb-5">
        <BadgesSection userId={userId} username={profile.username} />
      </div>

      {/* Friends section (own profile) */}
      {isOwn && (
        <div className="card p-5 mb-5">
          <div className="flex items-center gap-2 mb-4">
            <h2 className="font-serif font-semibold text-green-900 text-lg">Friends</h2>
            {pendingCount > 0 && (
              <span className="bg-red-500 text-white text-xs font-bold w-5 h-5 rounded-full flex items-center justify-center">
                {pendingCount}
              </span>
            )}
          </div>
          <FriendsSection isOwn={true} viewedUserId={userId} authUserId={authUser?.id} />
        </div>
      )}

      {/* Scores */}
      <h2 className="font-serif font-semibold text-green-900 text-lg mb-3">Score History</h2>

      {scores.length === 0 ? (
        <EmptyState
          icon="📋"
          title="No scores yet"
          message={isOwn ? "Head to a leaderboard to post your first score!" : `${profile.username} hasn't posted any scores yet.`}
          action={isOwn && <button onClick={() => navigate('/')} className="btn-gold">View Leaderboards</button>}
        />
      ) : (
        <div className="space-y-4">
          {Object.values(byCourse).map(courseGroup => (
            <div key={courseGroup.course_id} className="card overflow-hidden">
              <div className="px-4 py-3 bg-green-50 border-b border-amber-100">
                <p className="font-serif font-semibold text-green-900">
                  {courseGroup.course_name}
                </p>
                <p className="text-xs text-gray-400 mt-0.5">
                  {[courseGroup.city, courseGroup.state, courseGroup.country].filter(Boolean).join(', ')}
                </p>
              </div>
              <div className="divide-y divide-amber-50">
                {courseGroup.scores.map(score => (
                  <div key={score.score_id} className="px-4 py-3 flex items-center gap-3">
                    <RankBadge rank={score.place} />
                    <Link
                      to={`/leaderboard/course/${score.course_id}`}
                      className="flex-1 hover:opacity-80 transition-opacity min-w-0"
                    >
                      <div className="flex items-center gap-2">
                        <span
                          className="w-3 h-3 rounded-full border border-gray-200 flex-shrink-0"
                          style={{
                            backgroundColor: score.tee_color?.toLowerCase() === 'white'
                              ? '#f5f5f5'
                              : score.tee_color?.toLowerCase()
                          }}
                        />
                        <span className="text-sm font-medium text-green-800 capitalize truncate">
                          {score.tee_color} · {score.tee_gender}
                        </span>
                      </div>
                      <div className="text-xs text-gray-400 mt-0.5">
                        Par {score.par} · {score.yardage?.toLocaleString()} yds · Rating {score.rating}
                      </div>
                      <div className="text-xs text-gray-400">{formatDate(score.date_posted)}</div>
                      {score.friend_post_status === 'pending_player' && (
                        <div className="inline-flex items-center gap-1 mt-0.5 bg-amber-50 border border-amber-200 text-amber-700 text-xs font-medium rounded-full px-2 py-0.5">
                          📤 Pending your review
                        </div>
                      )}
                      {score.friend_post_status === 'accepted' && score.posted_by_friend && (
                        <div className="inline-flex items-center gap-1 mt-0.5 bg-green-50 border border-green-200 text-green-700 text-xs font-medium rounded-full px-2 py-0.5">
                          ✓ Posted by friend
                        </div>
                      )}
                    </Link>
                    <div className="text-right flex-shrink-0">
                      <div className="font-serif font-bold text-green-900">{score.total_score}</div>
                      <ScoreBadge overUnder={score.over_under_par} />
                    </div>
                    {/* Share button — visible to everyone */}
                    <ShareScoreButton score={score} profile={profile} />
                    {/* Delete button — only visible on own profile */}
                    {isOwn && (
                      <button
                        onClick={() => setScoreToDelete(score)}
                        className="flex-shrink-0 text-gray-300 hover:text-red-500 transition-colors p-1 rounded ml-1"
                        title="Delete this score"
                      >
                        🗑
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Edit profile modal */}
      {showEdit && (
        <EditProfileModal
          profile={profile}
          onClose={() => setShowEdit(false)}
          onSaved={() => { setShowEdit(false); fetchData(); }}
        />
      )}

      {/* Delete score confirmation modal */}
      {scoreToDelete && (
        <DeleteScoreConfirm
          score={scoreToDelete}
          onCancel={() => setScoreToDelete(null)}
          onConfirmed={() => { setScoreToDelete(null); fetchData(); }}
        />
      )}
    </div>
  );
}